import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'

import { fetchBridgeTokenById } from '@/api/modules'
import CopyButtonWithLabel from '@/common/CopyButtonWithLabel'
import GradientWrapper from '@/common/GradientWrapper'
import TokenSelect from '@/common/TokenSelect'
import { downloadJson } from '@/helpers'
import ProposalPreview from '@/pages/Multisig/components/ProposalPreview'
import { useSubmitMultisigProposal } from '@/pages/Multisig/CreateProposal/hooks/submit-multisig-proposal'
import { UiButton } from '@/ui/UiButton'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'

import {
  buildUpdateTokenMessage,
  UpdateTokenFormValues,
} from './tokenMessageBuilder'

const updateTokenSchema = z.object({
  creator: z.string().min(1, 'Creator address is required'),
  selectedTokenId: z.string().optional(),
  tokenId: z
    .string()
    .min(1, 'Token ID is required')
    .refine(
      async val => {
        try {
          await fetchBridgeTokenById(val)
          return true
        } catch {
          return false
        }
      },
      { message: 'Token with this ID does not exist' },
    ),
  metadata: z.object({
    name: z.string().min(1, 'Token name is required'),
    symbol: z.string().min(1, 'Symbol is required'),
    uri: z.string().optional(),
    dexName: z.string().optional(),
  }),
})

export default function UpdateTokenForm() {
  const { account } = useParams<{ account: string }>()

  const form = useForm<UpdateTokenFormValues>({
    resolver: zodResolver(updateTokenSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      creator: '',
      selectedTokenId: '',
      tokenId: '',
      metadata: {
        name: '',
        symbol: '',
        uri: '',
        dexName: '',
      },
    },
  })

  const submitProposalTx = useSubmitMultisigProposal({ group: account ?? '' })

  useEffect(() => {
    form.setValue('creator', account ?? '', { shouldValidate: true })
  }, [account, form])

  const watchedValues = useWatch({ control: form.control })

  const messagePayload = useMemo(() => {
    if (!form.formState.isValid || !watchedValues) return null

    try {
      return buildUpdateTokenMessage(watchedValues as UpdateTokenFormValues)
    } catch {
      return null
    }
  }, [form.formState.isValid, watchedValues])

  const generatedJson = useMemo(() => {
    if (!messagePayload) return null
    return JSON.stringify(messagePayload, null, 2)
  }, [messagePayload])

  const isSubmitting = submitProposalTx.isPending
  const isSubmitDisabled = !form.formState.isValid || isSubmitting

  return (
    <div className='space-y-6'>
      <div className='space-y-2'>
        <h3 className='text-foreground text-lg font-semibold'>
          Update Token Message
        </h3>
        <p className='text-muted-foreground text-sm'>
          Fill in the form to generate a message for updating token metadata
        </p>
      </div>

      <form
        onSubmit={event => event.preventDefault()}
        className='flex flex-col gap-6'
      >
        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='creator'>Creator Address</UiLabel>
          <UiInput
            id='creator'
            placeholder='Connect wallet'
            readOnly
            disabled={isSubmitting}
            className='bg-input border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
            {...form.register('creator')}
          />
          {form.formState.errors.creator && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.creator.message}
            </p>
          )}
        </div>

        <div className='flex flex-col gap-3'>
          <UiLabel>Select Token</UiLabel>
          <TokenSelect
            control={form.control}
            name='selectedTokenId'
            onValueChange={tokenId =>
              form.setValue('tokenId', tokenId, { shouldValidate: true })
            }
            onTokenSelect={selectedToken => {
              form.setValue('tokenId', selectedToken.id, {
                shouldValidate: true,
              })
              form.setValue(
                'metadata.name',
                selectedToken.metadata?.name ?? '',
                {
                  shouldValidate: true,
                },
              )
              form.setValue(
                'metadata.symbol',
                selectedToken.metadata?.symbol ?? '',
                {
                  shouldValidate: true,
                },
              )
              form.setValue('metadata.uri', selectedToken.metadata?.uri ?? '', {
                shouldValidate: true,
              })
              form.setValue(
                'metadata.dexName',
                selectedToken.metadata?.dexName ?? '',
                {
                  shouldValidate: true,
                },
              )
            }}
            disabled={isSubmitting}
            placeholder='Select a token to update'
          />
        </div>

        <div className='flex flex-col gap-3'>
          <UiLabel htmlFor='tokenId'>Token ID</UiLabel>
          <GradientWrapper>
            <UiInput
              id='tokenId'
              placeholder='1'
              disabled={isSubmitting}
              readOnly
              className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
              {...form.register('tokenId')}
            />
          </GradientWrapper>
          {form.formState.errors.tokenId && (
            <p className='text-destructive text-sm'>
              {form.formState.errors.tokenId.message}
            </p>
          )}
        </div>

        <div className='border-border space-y-3 rounded-lg border p-3'>
          <h4 className='text-foreground font-medium'>Token Metadata</h4>

          <div className='flex flex-col gap-3'>
            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='name'>Token Name</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='name'
                  placeholder='Ethereum'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                  {...form.register('metadata.name')}
                />
              </GradientWrapper>
              {form.formState.errors.metadata?.name && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.metadata.name.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='symbol'>Symbol</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='symbol'
                  placeholder='ETH'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                  {...form.register('metadata.symbol')}
                />
              </GradientWrapper>
              {form.formState.errors.metadata?.symbol && (
                <p className='text-destructive text-sm'>
                  {form.formState.errors.metadata.symbol.message}
                </p>
              )}
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='uri'>URI (Optional)</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='uri'
                  placeholder='https://...'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                  {...form.register('metadata.uri')}
                />
              </GradientWrapper>
            </div>

            <div className='flex flex-col gap-2'>
              <UiLabel htmlFor='dexName'>DEX Name (Optional)</UiLabel>
              <GradientWrapper>
                <UiInput
                  id='dexName'
                  placeholder='Uniswap'
                  disabled={isSubmitting}
                  className='bg-input text-foreground border-border h-12 w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
                  {...form.register('metadata.dexName')}
                />
              </GradientWrapper>
            </div>
          </div>
        </div>
      </form>

      {messagePayload && <ProposalPreview messages={messagePayload.messages} />}

      <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <div className='flex flex-wrap items-center gap-2'>
          {generatedJson && (
            <>
              <CopyButtonWithLabel
                value={generatedJson}
                label='Copy JSON'
                copiedLabel='Copied'
                tooltip='Copy JSON'
              />
              <UiButton
                type='button'
                variant='outline'
                size='sm'
                onClick={() =>
                  downloadJson(messagePayload, 'update-token-message.json')
                }
                disabled={isSubmitting}
              >
                Download JSON
              </UiButton>
            </>
          )}
        </div>
        <UiButton
          type='button'
          disabled={isSubmitDisabled}
          onClick={form.handleSubmit(values => {
            const messageObject = buildUpdateTokenMessage(values)
            submitProposalTx.mutate({
              rawJsonText: JSON.stringify(messageObject, null, 2),
            })
          })}
        >
          {isSubmitting ? 'Submitting…' : 'Submit proposal'}
        </UiButton>
      </div>
    </div>
  )
}
